import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { type Claim, patchClaimRequest } from '../../../../models/claim'
import { ClaimCargoBrokerSection } from './ClaimCargoBrokerSection'
import { ClaimPhotoExtractionBanner } from './ClaimPhotoExtractionBanner'
import { claimToStep3Defaults, STEP3_DEFAULTS, STEP3_GROUPS, type Step3FormValues } from './claimStep3Values'
import { ClaimStepShell } from './ClaimStepShell'
import { ClaimVehicleStatusSection } from './ClaimVehicleStatusSection'
import { ClaimWhatHappenedSection } from './ClaimWhatHappenedSection'
import { ClaimWhenWhereSection } from './ClaimWhenWhereSection'
import { mergeExtractedValues } from './mergeExtractedValues'
import { FILLED_PREFIXES, filledFromPhotos, useClaimPhotoExtraction } from './useClaimPhotoExtraction'

const pad = (n: number) => String(n).padStart(2, '0')

interface ClaimIncidentDetailsFormProps {
  claimId: string
  /** On-behalf org scope (CAI user acting for a customer) — used on the save request. */
  orgId?: string
  claim?: Claim
  onBack: () => void
  onSaved: (claim: Claim) => void
}

export function ClaimIncidentDetailsForm({
  claimId,
  orgId,
  claim,
  onBack,
  onSaved,
}: ClaimIncidentDetailsFormProps) {
  const [saving, setSaving] = useState(false)
  const [initial] = useState(() => (claim ? claimToStep3Defaults(claim) : STEP3_DEFAULTS))
  const { control, register, handleSubmit, setValue, formState, reset, getValues } = useForm<Step3FormValues>(
    {
      defaultValues: initial,
    }
  )
  const errors = formState.errors

  const { extracted, reading, latest } = useClaimPhotoExtraction(claim, orgId)
  useEffect(() => {
    if (!extracted) return
    reset(mergeExtractedValues(claimToStep3Defaults(extracted), getValues(), initial, STEP3_GROUPS))
  }, [extracted, reset, getValues, initial])

  async function onValid(v: Step3FormValues) {
    setSaving(true)
    try {
      const saved = await patchClaimRequest(
        claimId,
        {
          incident: {
            date: v.date ? format(v.date, 'yyyy-MM-dd') : null,
            time: v.time ? `${pad(v.time.hours)}:${pad(v.time.minutes)}:00` : null,
            city: v.city,
            state: v.state,
            locationDescription: v.location,
            whatHappened: v.whatHappened,
            howManyVehicles: v.howManyVehicles,
            whatDamaged: v.whatDamaged,
            briefDescription: v.briefDescription,
            cargoOnBoard: v.cargoOnBoard,
            freightBrokerName: v.freightBrokerName,
            freightBrokerContact: v.freightBrokerContact,
            truckDriveable: v.truckDriveable,
            vehiclesNeedingTow: v.vehiclesNeedingTow,
            loadStatus: v.loadStatus,
            anythingElse: v.anythingElse,
          },
        },
        orgId
      )
      onSaved(saved)
    } catch {
      toast.error('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ClaimStepShell
      step={3}
      title="Incident Details"
      subtitle="Tell us what happened. Not sure of the exact time? Leave it blank."
    >
      <ClaimPhotoExtractionBanner
        reading={reading}
        filled={filledFromPhotos(latest, FILLED_PREFIXES.incident)}
      />

      <Card>
        <CardHeader>
          <CardTitle>When &amp; where</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <ClaimWhenWhereSection control={control} setValue={setValue} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What happened</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <ClaimWhatHappenedSection control={control} register={register} errors={errors} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cargo &amp; broker information</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <ClaimCargoBrokerSection control={control} register={register} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vehicle &amp; load status</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <ClaimVehicleStatusSection control={control} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Anything else we should know?</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={4}
            placeholder="Any other details — damage specifics, cargo location, witnesses, road hazards, special circumstances, or anything else relevant to this incident."
            {...register('anythingElse')}
          />
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          ← Back
        </Button>
        <Button type="button" onClick={handleSubmit(onValid)} loading={saving ? 'Saving…' : undefined}>
          Next →
        </Button>
      </div>
    </ClaimStepShell>
  )
}
